import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import { FacebookAuthProvider, GoogleAuthProvider } from '@angular/fire/auth'
import { Subject } from 'rxjs';
import { AuthType, LocalStorageKey } from 'src/app/utils';
import { LocalStorageService } from '../services.module';


@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private googleAuthProvider: GoogleAuthProvider;
    private facebookAuthProvider: FacebookAuthProvider;
    loggedInUser: firebase.User | null = null;
    private onUserAuthStateChanged = new Subject<string>();
    onUserAuthStateChangedEvent$ = this.onUserAuthStateChanged.asObservable();
    private authStateUnsubscribe: any;

    constructor(
        private fireAuth: AngularFireAuth,
        private localStorageService: LocalStorageService,
    ) {
        this.googleAuthProvider = new GoogleAuthProvider();
        this.facebookAuthProvider = new FacebookAuthProvider();
        this.onAuthStateChanged();
    }

    async onAuthStateChanged(): Promise<void> {
        this.authStateUnsubscribe = await this.fireAuth.onAuthStateChanged(async (user) => {
            if (user) {
                this.finishUserLoggedIn(user);
                const token = await this.getUIDToken();
            } else {
                this.loggedInUser = null;
                this.onUserAuthStateChanged.next('');
            }
        })
    }

    private finishUserLoggedIn(user: firebase.User) {
        this.setLoggedInUserInfo(user);
        // if (this.authStateUnsubscribe) {
        //     this.authStateUnsubscribe();
        // }
    }

    private setLoggedInUserInfo(user: firebase.User) {
        this.loggedInUser = user;
    }

    async getLoggedInUserInfo() {
        if (this.loggedInUser) {
            return this.loggedInUser
        } else {
            const loggedInUser = await this.fireAuth.currentUser;
            return loggedInUser;
        }
    }

    async getUIDToken(): Promise<string | null> {
        let token = null;
        try {
            const user = await this.fireAuth.currentUser;
            if (user) {
                token = await user.getIdToken(true);
                this.onUserAuthStateChanged.next(token)
            }
        } catch (error) {
            console.log(error);
        } finally {
            return token;
        }
    }

    async signOut() {
        try {
            this.loggedInUser = null;
            this.fireAuth.signOut();
            this.onUserAuthStateChanged.next('');
        } catch (error) {
            console.log(error);
        }
    }

    createAccountWithEmailAndPassword(email: string, password: string, first_name: string, last_name: string) {
        return new Promise((resolve, reject) => {
            this.fireAuth.createUserWithEmailAndPassword(email, password).then(async result => {
                if (result.user) {
                    await result.user.updateProfile({
                        displayName: first_name + ' ' + last_name
                    })
                }
                this.onAuthStateChanged();
                resolve(true)
            }).catch(async (error: firebase.FirebaseError) => {
                reject(error);
            })
            // this.fireAuth.setPersistence(firebase.auth.Auth.Persistence.LOCAL)
        });
    }

    loginWithEmailAndPassword(email: string, password: string) {
        return new Promise((resolve, reject) => {
            this.fireAuth.signInWithEmailAndPassword(email, password).then(result => {
                resolve(true);
            }).catch(async (error: firebase.FirebaseError) => {
                switch (error.code) {
                    case 'auth/user-not-found': {
                        const method = await this.fireAuth.fetchSignInMethodsForEmail(email);
                        if (method && method.length && method.includes('google.com')) {
                            error.code = 'auth/google-auth';
                        }
                        if (method && method.length && method.includes('facebook.com')) {
                            error.code = 'auth/facebook-auth';
                        }
                        reject(error);
                        break
                    }
                    default: {
                        reject(error);
                    }
                }
            })
        })
    }

    sendPasswordRecoveryEmail(email: string) {
        return new Promise((resolve, reject) => {
            this.fireAuth.sendPasswordResetEmail(email).then(result => {
                resolve(true);
            }).catch(async (error: firebase.FirebaseError) => {
                reject(error);
            })
            this.fireAuth.setPersistence
        })
    }

    async authenticateWithGoogle() {
        const userEmail: string | null = this.localStorageService.getItem(LocalStorageKey.USER_EMAIL_ADDRESS);
        if (userEmail) this.googleAuthProvider.setCustomParameters({ login_hint: userEmail });

        return new Promise((resolve, reject) => {
            this.fireAuth.signInWithPopup(this.googleAuthProvider).then(result => {
                resolve(true)
            }).catch(async (error: firebase.FirebaseError) => {
                reject(error);
            });
        });
    }

    authenticateWithFacebook() {
        return new Promise((resolve, reject) => {
            this.fireAuth.signInWithPopup(this.facebookAuthProvider).then(result => {
                resolve(true)
            }).catch(async (error: firebase.FirebaseError) => {
                reject(error);
            });
        });
    }

    handleAuthErrorMessage(error: firebase.FirebaseError) {
        switch (error.code) {
            case 'auth/user-disabled': {
                return 'Your account has been disabled because of violations of our policies. If you think this is a mistake, contact us from help section.';
            }
            case 'auth/user-not-found': {
                return 'Couldn\'t find account with this email id, create account instead.';
            }
            case 'auth/wrong-password': {
                return 'Invalid password.';
            }
            case 'auth/google-auth': {
                return 'Log in with Google instead.';
            }
            case 'auth/facebook-auth': {
                return 'Log in with Facebook instead.';
            }
            case 'auth/invalid-email': {
                return 'Please enter a valid email address.';
            }
            case 'auth/email-already-in-use': {
                return 'Account already exists with this email, Log in instead.';
            }
            case 'auth/weak-password': {
                return 'Weak Password! Please user a strong password.';
            }
            default: {
                return 'Email or password seems invalid, please try again.';
            }
        }
    }

    // // Send email
    // this.fireAuth.currentUser.then((user) => {
    //     user.sendEmailVerification().then(() => {
    //       console.log('Verification email sent.');
    //     }).catch((error) => {
    //       console.error('Error while sending verification email: ', error);
    //     });
    //   });

    // //   Check if email verified
    // this.fireAuth.currentUser.then((user) => {
    //     if(user.emailVerified) {
    //       console.log('Email is verified');
    //     } else {
    //       console.log('Email is not verified');
    //     }
    //   });


    checkIfAccountExists(email: string): Promise<string[]> {
        return new Promise((resolve, reject) => {
            this.fireAuth.fetchSignInMethodsForEmail(email)
                .then(signInMethods => {
                    resolve(signInMethods)
                }).catch(async (error: firebase.FirebaseError) => {
                    reject(error)
                });
        })
    }
}
